import type { Node } from "@mapcraft-tasks/client-store";
import type { CSSProperties } from "react";
import { hasCheckbox } from "../lib/complete";

/**
 * Галочка ноды — один и тот же элемент везде, где нода вообще показана: в строке
 * аутлайна, в заголовке страницы и в панелях справа. Смысл «сделал» не должен
 * зависеть от того, в каком списке человек эту ноду увидел.
 *
 * У кого галочки нет (`plain`, системные роли) — компонент не рисует ничего, и
 * заголовок встаёт на место галочки сам: резервировать пустое место незачем,
 * в аутлайне это так и работает.
 */
export function Checkbox({
	node,
	done,
	size = 15,
	title,
	className = "self-start",
	style,
	onToggle
}: {
	node: Node;
	/**
	 * Показать закрашенной помимо статуса. Нужно повторяющейся: «сделано» у неё —
	 * это набранное x/N за период, а статус всегда `open`.
	 */
	done?: boolean;
	/** Сторона квадрата. Крупнее — только рядом с заголовком страницы. */
	size?: number;
	title?: string;
	/** Выравнивание во флекс-строке: список ставит галочку к первой строке, заголовок — по центру. */
	className?: string;
	style?: CSSProperties;
	onToggle: () => void;
}) {
	if (!hasCheckbox(node)) {
		return null;
	}
	const state = done || node.status === "done" ? "done" : node.status === "cancelled" ? "cancelled" : "open";
	return (
		<button
			type="button"
			aria-label="выполнено"
			title={title}
			className={`checkbox ${className}`}
			data-state={state}
			style={{ width: size, height: size, ...style }}
			onClick={(e) => {
				e.stopPropagation();
				onToggle();
			}}
		>
			{state === "done" && <Mark size={size} d="M20 6L9 17l-5-5" />}
			{state === "cancelled" && <Mark size={size} d="M6 6l12 12M18 6L6 18" />}
		</button>
	);
}

/**
 * Иконка рисуется в сетке 24 штрихом 3 и показывается долей от квадрата — тонкая
 * контурная, цвет наследуется от кнопки. Это рецепт иконок Tana целиком.
 */
function Mark({ size, d }: { size: number; d: string }) {
	const icon = Math.round(size * 0.73);
	return (
		<svg
			viewBox="0 0 24 24"
			width={icon}
			height={icon}
			fill="none"
			stroke="currentColor"
			strokeWidth={3}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d={d} />
		</svg>
	);
}
